import router from '@app/router/index.APP_TARGET';
import Vue from 'vue';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import store from '@app/store';
import { Center } from '@core/interfaces/tenant-state';
import jwt from 'jsonwebtoken';
import { Observable } from 'rxjs';
import { Invoice } from '@accounting/interfaces/invoice';

export default class AuthService extends Vue {
  public token: string;
  public refreshToken: string;
  public isRefreshing = false;
  public isSuperAdmin: boolean;
  public tenants: Center[];

  constructor() {
    super();
  }

  // NOTE: Is better to use the other method get loggedIn as a property
  isLoggedIn(): boolean {
    return store.getters['auth/isLoggedIn'];
  }

  get loggedIn(): boolean {
    return store.getters['auth/isLoggedIn'];
  }

  getToken(): string {
    return store.getters['auth/authToken'];
  }
  getExpiresAt(): number | null {
    return store.getters['auth/expiresAt'];
  }
  setToken(token: string): void {
    this.token = token;
    store.commit('auth/setToken', token);
  }
  clearToken(): void {
    this.token = null;
    store.commit('auth/clearToken');
  }
  getRefreshToken(): string {
    return store.getters['auth/refreshToken'];
  }
  setRefreshToken(refreshToken: string): void {
    this.refreshToken = refreshToken;
    store.commit('auth/setRefreshToken', refreshToken);
  }
  clearRefreshToken(): void {
    this.refreshToken = null;
    store.commit('auth/clearRefreshToken');
  }
  getIsSuperAdmin(): boolean {
    return store.getters['auth/isSuperAdmin'];
  }

  isAdmin(): boolean {
    return ['admin', 'superadmin'].includes(this.getMainRole());
  }
  isGuard(): boolean {
    return ['guard'].includes(this.getMainRole());
  }
  setIsSuperAdmin(isSuperAdmin: boolean): void {
    this.isSuperAdmin = isSuperAdmin;
    store.commit('auth/setIsSuperAdmin', isSuperAdmin);
  }
  clearIsSuperAdmin(): void {
    this.isSuperAdmin = null;
    store.commit('auth/clearIsSuperAdmin');
  }
  getTenants(): Center[] {
    return store.getters['tenant/tenants'];
  }
  setTenants(tenants: Center[]): void {
    this.tenants = tenants;
    store.commit('tenant/storeTenants', tenants);
  }
  getTenantId(): void {
    return store.getters['tenant/selectedTenantId'];
  }
  setTenantId(tenants: Center[]): void {
    store.commit('tenant/selectTenant', tenants[0]);
  }
  getSelectedTenant(): Center {
    // NOTE now selected tenant do not have center_setting
    // access to center_setting via store.getters['centerSettings/centerSettings']
    return store.getters['tenant/selectedTenant'];
  }
  getPermissions() {
    const tenantId = this.getTenantId();
    return store.getters['auth/permissions']({ tenantId: tenantId });
  }
  getRoles() {
    const tenantId = this.getTenantId();
    return store.getters['auth/roles']({ tenantId: tenantId });
  }
  getContactId() {
    const tenantId = this.getTenantId();
    return store.getters['auth/contactId']({ tenantId: tenantId });
  }
  getIsIndividual(): boolean {
    const tenantId = this.getTenantId();
    const profile = store.getters['auth/profile']({ tenantId: tenantId });
    return profile.contact_type === 'individual';
  }
  getMainRole() {
    const tenantId = this.getTenantId();
    return store.getters['auth/mainRole']({ tenantId: tenantId });
  }
  getIsAllowedToOnlineOrganizationBooking(): boolean {
    const tenantId = this.getTenantId();
    const profile = store.getters['auth/profile']({ tenantId: tenantId });
    return ['requested', 'confirmed'].includes(profile.allow_online_organization_booking);
  }
  allowedTypeForOrganizationBooking(): string {
    const tenantId = this.getTenantId();
    const profile = store.getters['auth/profile']({ tenantId: tenantId });
    return profile.allow_online_organization_booking;
  }
  allowedToPayOnlineInvoices(invoiceToPay: Invoice = null): boolean {
    const tenantId = this.getTenantId();
    const profile = store.getters['auth/profile']({ tenantId: tenantId });
    let allowed;
    if (profile) {
      allowed = profile.allow_online_invoice_payment;
    } else {
      allowed = invoiceToPay?.contact?.allow_online_invoice_payment;
    }
    return allowed;
  }
  handlerAuthError() {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.userRefreshToken();
    }
  }

  hasTokenExpired(): boolean {
    const token = store.getters['auth/authToken'];
    const securityMilliSeconds = 120000; // 2 min
    if (!token) {
      return true;
    }
    const decodedToken = jwt.decode(token);
    const tokenExpireTime = decodedToken.exp * 1000 - securityMilliSeconds;
    return Date.now() >= tokenExpireTime;
  }

  hasRefreshToken(): boolean {
    const refreshToken = this.getRefreshToken();

    return !!refreshToken;
  }

  public refreshTokenObservable: Observable<BPCGenericApiResponse>;

  userRefreshToken(): Promise<string> {
    this.isRefreshing = true;
    return new Promise((resolve, reject) => {
      const refreshPath = this.$bpcApiRouteHelper.getRefreshTokenPath();
      if (!this.refreshTokenObservable) {
        console.log('CREATING A refreshTokenObservable');
        this.refreshTokenObservable = this.$bpcApi.post(
          refreshPath,
          {
            refresh_token: this.getRefreshToken(),
            user: store.getters['auth/user'],
          },
          { headers: { BPC_Refresh_token_request: true } },
        );
      }

      this.refreshTokenObservable.subscribe(
        (response: BPCGenericApiResponse) => {
          // If the token is refresh we stored it and we continue
          this.$authService.setToken(response['data']['access_token']);
          this.$authService.setRefreshToken(response['data']['refresh_token']);
          this.$authService.setIsSuperAdmin(response['data']['super_admin']);
          this.$authService.setTenants(response['data']['centers']);
          this.$authService.setTenantId(response['data']['centers']);
          this.isRefreshing = false;
          this.refreshTokenObservable = null;
          resolve(response['data']['access_token']);
        },
        error => {
          // If the refresh token isn't valid we redirect to login page
          this.clearToken();
          this.clearRefreshToken();
          this.clearIsSuperAdmin();
          this.isRefreshing = false;
          this.$bpcMessage.showErrorMessage({ text: 'global.auth.must_login' });
          this.refreshTokenObservable = null;
          const loginRoute = this.$authorizationService.getAuthLoginRoute();
          router.push(loginRoute);
        },
      );
    });
  }

  navigateToTenantWithAuthByQuery(
    tenantAlias: string,
    refreshToken: string = null,
    redirectUrl: string = null,
  ) {
    const protocol = window.location.protocol;
    const domain = this.$clientRouter.getTenantDomain(tenantAlias);
    const queryAuthPath = '/qauth';
    const token = refreshToken || this.getRefreshToken();
    const queryArray = [`?refreshToken=${token}`];
    if (redirectUrl) {
      queryArray.push(`redirect=${redirectUrl}`);
    }
    const query = queryArray.join('&');
    const queryAuthFullURL = `${protocol}//${domain}${queryAuthPath}${query}`;
    if (this.$clientRouter.isLocalHost()) {
      console.log('Navigate to:', queryAuthFullURL);
    }
    window.location.href = queryAuthFullURL;
  }
}
